import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Save, Settings2, CheckCircle2, XCircle, Plus } from "lucide-react";

const REPAIR_STATUSES = [
  "received",
  "item_received",
  "diagnosed",
  "awaiting_approval",
  "in_repair",
  "completed",
  "closed",
];

const STATUS_COLORS = {
  received:          "bg-gold/20 text-gold",
  item_received:     "bg-amber-400/20 text-amber-300",
  diagnosed:         "bg-sky-500/20 text-sky-300",
  awaiting_approval: "bg-purple-500/20 text-purple-300",
  in_repair:         "bg-violet-500/20 text-violet-300",
  completed:         "bg-emerald-500/20 text-emerald-300",
  closed:            "bg-slate-500/20 text-slate-400",
};

const STATUS_LABEL = {
  received:          "Received",
  item_received:     "Item Received",
  diagnosed:         "Diagnosed",
  awaiting_approval: "Awaiting Approval",
  in_repair:         "In Repair",
  completed:         "Completed",
  closed:            "Closed",
};

// Status descriptions shown as hints in the modal
const STATUS_HINTS = {
  received:          "Enquiry submitted. Awaiting physical item.",
  item_received:     "Device is in store. Begin diagnosis.",
  diagnosed:         "Diagnosis done — set estimated cost and move to Awaiting Approval to notify customer.",
  awaiting_approval: "Quote sent to customer. Waiting for their approval.",
  in_repair:         "Customer approved. Repair in progress.",
  completed:         "Repair done. Ready to ship / collect COD.",
  closed:            "Enquiry closed (customer declined or cancelled).",
};

// issueId → hall_effect: true means this issue also has a hall effect add-on key
const REPAIR_OPTIONS_KEYS = {
  controller: [
    { id: "left_drift",  hall_effect: true },
    { id: "right_drift", hall_effect: true },
    { id: "buttons",     hall_effect: false },
  ],
  console: [
    { id: "disc_drive",      hall_effect: false },
    { id: "power",           hall_effect: false },
    { id: "hdmi",            hall_effect: false },
    { id: "general_service", hall_effect: false },
    { id: "overheating",     hall_effect: false },
  ],
};

const ISSUE_LABELS = {
  left_drift: "Left Stick Drift", right_drift: "Right Stick Drift", buttons: "Button Repair",
  disc_drive: "Disc Drive", power: "Power Issue", hdmi: "HDMI Port",
  general_service: "Servicing", overheating: "Overheating",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${STATUS_COLORS[status] ?? "bg-slate-700 text-slate-300"}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}

function StatusHistory({ history }) {
  if (!history?.length) return null;
  return (
    <div className="mb-5">
      <div className="text-xs font-bold uppercase tracking-wider text-gold mb-2">Status History</div>
      <div className="relative pl-4 space-y-0">
        {history.map((h, idx) => (
          <div key={idx} className="relative flex items-start gap-3 pb-3 last:pb-0">
            <div className="absolute left-0 top-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
            {idx < history.length - 1 && (
              <div className="absolute left-[2.5px] top-3 bottom-0 w-px bg-gold/20" />
            )}
            <div className="pl-3">
              <StatusBadge status={h.status} />
              <div className="text-[10px] text-slate-500 mt-0.5">{fmtDate(h.timestamp)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IssuesList({ issues }) {
  if (!issues?.length) return <span className="text-slate-500">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {issues.map((i) => (
        <span key={i.id} className="text-[10px] font-bold uppercase tracking-wider bg-gold/10 text-gold border border-gold/20 rounded-full px-1.5 py-0.5">
          {i.label}{i.hall_effect ? " + Hall Effect" : ""}
        </span>
      ))}
    </div>
  );
}

const REPAIR_OPTIONS = {
  controller: [
    { id: "left_drift",  label: "Left Stick Drift",  hall_effect: true },
    { id: "right_drift", label: "Right Stick Drift", hall_effect: true },
    { id: "buttons",     label: "Button Repair",      hall_effect: false },
  ],
  console: [
    { id: "disc_drive",      label: "Disc Drive",   hall_effect: false },
    { id: "power",           label: "Power Issue",  hall_effect: false },
    { id: "hdmi",            label: "HDMI Port",    hall_effect: false },
    { id: "general_service", label: "Servicing",    hall_effect: false },
    { id: "overheating",     label: "Overheating",  hall_effect: false },
  ],
};

const BLANK_NEW = {
  customer_name: "", customer_email: "", customer_phone: "",
  device_type: "controller", device_name: "",
  issues: [], notes: "", pickup_required: false, address: "", walk_in: true,
};

export default function AdminRepairs() {
  const [view, setView] = useState("list");
  const [enquiries, setEnquiries] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ status: "", estimated_cost: "", final_cost: "", admin_notes: "", payment_method: "" });
  const [saving, setSaving] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({});
  const [savingPricing, setSavingPricing] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(BLANK_NEW);
  const [creatingNew, setCreatingNew] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/repair/enquiries");
    setEnquiries(r.data);
  };

  const loadPricing = async () => {
    const r = await api.get("/admin/repair/pricing");
    setPricingDraft(r.data);
  };

  useEffect(() => { load(); loadPricing(); }, []);

  function openEnquiry(e) {
    setActive(e);
    setForm({
      status: e.status,
      estimated_cost: e.estimated_cost ?? "",
      final_cost: e.final_cost ?? "",
      admin_notes: e.admin_notes ?? "",
      payment_method: e.payment_method ?? "",
    });
  }

  // Only allow advancing forward in the status chain
  function allowedStatuses(currentStatus) {
    const idx = REPAIR_STATUSES.indexOf(currentStatus);
    return REPAIR_STATUSES.slice(Math.max(0, idx));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.estimated_cost !== "") payload.estimated_cost = parseFloat(payload.estimated_cost);
      else delete payload.estimated_cost;
      if (payload.final_cost !== "") payload.final_cost = parseFloat(payload.final_cost);
      else delete payload.final_cost;
      if (!payload.payment_method) delete payload.payment_method;
      await api.patch(`/admin/repair/enquiries/${active.id}`, payload);
      toast.success("Updated — customer notified");
      setActive(null);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  function setPriceField(deviceType, issueId, field, value) {
    const key = `${deviceType}_${issueId}`;
    setPricingDraft((p) => ({ ...p, [key]: { ...(p[key] ?? {}), [field]: value ? parseFloat(value) : "" } }));
  }

  async function savePricing() {
    setSavingPricing(true);
    try {
      await api.put("/admin/repair/pricing", pricingDraft);
      toast.success("Pricing saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingPricing(false); }
  }

  function toggleNewIssue(issue) {
    setNewForm((f) => {
      const exists = f.issues.find((i) => i.id === issue.id);
      if (exists) return { ...f, issues: f.issues.filter((i) => i.id !== issue.id) };
      return { ...f, issues: [...f.issues, { id: issue.id, label: issue.label }] };
    });
  }

  function toggleHallEffect(issue) {
    setNewForm((f) => ({
      ...f,
      issues: f.issues.map((i) =>
        i.id === issue.id ? { ...i, hall_effect: !i.hall_effect } : i
      ),
    }));
  }

  async function createRepair() {
    if (!newForm.customer_name.trim() || !newForm.device_name.trim() || !newForm.issues.length) {
      toast.error("Fill in customer name, device name, and at least one issue");
      return;
    }
    setCreatingNew(true);
    try {
      await api.post("/repair/enquiries", newForm);
      toast.success("Repair entry created");
      setShowNew(false);
      setNewForm(BLANK_NEW);
      await load();
    } catch { toast.error("Failed to create entry"); }
    finally { setCreatingNew(false); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Service</div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Repair Enquiries</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView("list")} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition ${view === "list" ? "bg-gold text-navy-900" : "border border-gold/30 text-gold hover:bg-gold/10"}`}>
            Enquiries
          </button>
          <button onClick={() => setView("pricing")} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition ${view === "pricing" ? "bg-gold text-navy-900" : "border border-gold/30 text-gold hover:bg-gold/10"}`}>
            <Settings2 className="w-3.5 h-3.5" /> Pricing
          </button>
          <button onClick={() => { setShowNew(true); setNewForm(BLANK_NEW); }} className="flex items-center gap-1.5 bg-gold text-navy-900 text-xs font-extrabold uppercase tracking-wider px-4 py-2 rounded-lg hover:bg-gold-light transition">
            <Plus className="w-3.5 h-3.5" /> New Repair
          </button>
        </div>
      </div>

      {view === "list" && (
        <>
          <div className="bg-navy-800 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gold/10 text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-5 py-3">Enquiry</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Device</th>
                  <th className="text-left px-5 py-3 hidden lg:table-cell">Issues</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3 hidden xl:table-cell">Approval</th>
                  <th className="text-left px-5 py-3 hidden xl:table-cell">Cost</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-500 text-sm">No repair enquiries yet</td></tr>
                )}
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-b border-gold/5 hover:bg-navy-700/40 transition">
                    <td className="px-5 py-3 font-mono text-xs text-gold whitespace-nowrap">{e.enquiry_number}</td>
                    <td className="px-5 py-3">
                      <div className="font-bold text-xs">{e.customer_name}</div>
                      <div className="text-[10px] text-slate-500">{e.customer_phone}</div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-xs text-slate-300">{e.device_name}</td>
                    <td className="px-5 py-3 hidden lg:table-cell max-w-[180px]">
                      <IssuesList issues={e.issues} />
                    </td>
                    <td className="px-5 py-3"><StatusBadge status={e.status} /></td>
                    <td className="px-5 py-3 hidden xl:table-cell">
                      {e.customer_approved === true && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" /> Approved
                        </span>
                      )}
                      {e.customer_approved === false && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-rose-400">
                          <XCircle className="w-3 h-3" /> Declined
                        </span>
                      )}
                      {e.customer_approved == null && e.status === "awaiting_approval" && (
                        <span className="text-[10px] text-slate-500">Pending…</span>
                      )}
                    </td>
                    <td className="px-5 py-3 hidden xl:table-cell text-xs text-slate-300 whitespace-nowrap">
                      {e.final_cost != null ? `₹${e.final_cost.toLocaleString("en-IN")}` : e.estimated_cost != null ? `~₹${e.estimated_cost.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => openEnquiry(e)} className="text-xs font-bold text-gold hover:underline whitespace-nowrap">
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {active && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
              <div className="bg-navy-800 border border-gold/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-bold uppercase tracking-wider text-gold">{active.enquiry_number}</div>
                      {active.walk_in && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded-full">Walk-in</span>
                      )}
                    </div>
                    <div className="text-lg font-black mt-1">{active.device_name}</div>
                    <div className="mt-1.5"><IssuesList issues={active.issues} /></div>
                  </div>
                  <button onClick={() => setActive(null)}><X className="w-5 h-5 text-slate-400 hover:text-white" /></button>
                </div>

                {/* Customer approval callout */}
                {active.status === "awaiting_approval" && active.customer_approved == null && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl px-4 py-3 mb-5 text-xs text-purple-300">
                    ⏳ Waiting for customer to approve the quote of <strong>₹{active.estimated_cost?.toLocaleString("en-IN")}</strong>. They'll approve via the tracking portal link in their email.
                  </div>
                )}
                {active.customer_approved === true && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3 mb-5 text-xs text-emerald-300 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0" /> Customer approved the quote. You can now begin repair.
                  </div>
                )}
                {active.customer_approved === false && (
                  <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3 mb-5 text-xs text-rose-300 flex items-center gap-2">
                    <XCircle className="w-4 h-4 shrink-0" /> Customer declined the quote. Enquiry will be closed.
                  </div>
                )}

                {/* Status history */}
                <StatusHistory history={active.status_history} />

                {/* Customer info */}
                <div className="text-xs text-slate-400 space-y-1 mb-5 bg-navy-700/50 rounded-lg p-4">
                  <p><strong className="text-slate-300">Customer:</strong> {active.customer_name}</p>
                  {active.customer_email && <p><strong className="text-slate-300">Email:</strong> {active.customer_email}</p>}
                  <p><strong className="text-slate-300">Phone:</strong> {active.customer_phone}</p>
                  <p><strong className="text-slate-300">Method:</strong> {active.pickup_required ? `Pickup${active.pickup_discount ? ` (−₹${active.pickup_discount})` : ""}` : active.walk_in ? "Walk-in" : "Drop-off"}</p>
                  {active.address && <p><strong className="text-slate-300">Address:</strong> {active.address}</p>}
                  {active.notes && <p><strong className="text-slate-300">Notes:</strong> {active.notes}</p>}
                </div>

                <div className="space-y-4">
                  {/* Status */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white"
                    >
                      {allowedStatuses(active.status).map((s) => (
                        <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                      ))}
                    </select>
                    {STATUS_HINTS[form.status] && (
                      <p className="text-[10px] text-slate-500 mt-1.5">{STATUS_HINTS[form.status]}</p>
                    )}
                    {form.status === "awaiting_approval" && (
                      <p className="text-[10px] text-amber-400 mt-1.5">⚠ Setting this status will email the customer with the estimated cost and a link to approve or decline.</p>
                    )}
                  </div>

                  {/* Cost — single field, used as estimated until completed, then as final */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">
                      {form.status === "completed" ? "Final Cost (₹)" : "Quoted Cost (₹)"}
                    </label>
                    <input
                      type="number"
                      value={form.status === "completed" ? form.final_cost : form.estimated_cost}
                      onChange={(e) => setForm((f) =>
                        form.status === "completed"
                          ? { ...f, final_cost: e.target.value }
                          : { ...f, estimated_cost: e.target.value }
                      )}
                      className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white"
                      placeholder="0"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      {form.status === "completed"
                        ? "Final amount — collected COD on delivery"
                        : "Sent to customer when status = Awaiting Approval"}
                    </p>
                  </div>

                  {/* Payment method — only at completed */}
                  {form.status === "completed" && (
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Payment Method</label>
                      <select
                        value={form.payment_method}
                        onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}
                        className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white"
                      >
                        <option value="">— Not set —</option>
                        <option value="cod">Cash on Delivery / Collection</option>
                      </select>
                      <p className="text-[10px] text-slate-500 mt-1">Customer pays COD when device is shipped back / collected</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">
                      Admin Notes <span className="text-slate-500 font-normal normal-case">(emailed to customer on save)</span>
                    </label>
                    <textarea
                      value={form.admin_notes}
                      onChange={(e) => setForm((f) => ({ ...f, admin_notes: e.target.value }))}
                      rows={3}
                      className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white resize-none"
                      placeholder="E.g. Repair complete, shipping tomorrow…"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button onClick={() => setActive(null)} className="border border-gold/30 text-gold text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-gold/10 transition">
                    Cancel
                  </button>
                  <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-gold text-navy-900 text-xs font-extrabold uppercase tracking-wider px-6 py-2.5 rounded-full hover:bg-gold-light transition disabled:opacity-50">
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save & Notify"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-navy-800 border border-gold/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gold mb-1">Walk-in Customer</div>
                <h2 className="text-lg font-black uppercase tracking-tight">New Repair Entry</h2>
              </div>
              <button onClick={() => setShowNew(false)}><X className="w-5 h-5 text-slate-400 hover:text-white" /></button>
            </div>

            <div className="space-y-4">
              {/* Customer info */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Customer Name</label>
                <input
                  value={newForm.customer_name}
                  onChange={(e) => setNewForm((f) => ({ ...f, customer_name: e.target.value }))}
                  className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="Full name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Phone</label>
                  <input
                    value={newForm.customer_phone}
                    onChange={(e) => setNewForm((f) => ({ ...f, customer_phone: e.target.value }))}
                    className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Email <span className="text-slate-500 font-normal normal-case">(optional)</span></label>
                  <input
                    value={newForm.customer_email}
                    onChange={(e) => setNewForm((f) => ({ ...f, customer_email: e.target.value }))}
                    className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white"
                    placeholder="customer@email.com"
                  />
                </div>
              </div>

              {/* Device */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Device Type</label>
                <div className="flex gap-2">
                  {["controller", "console"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setNewForm((f) => ({ ...f, device_type: t, issues: [] }))}
                      className={`flex-1 text-xs font-bold uppercase tracking-wider py-2 rounded-lg border transition ${newForm.device_type === t ? "bg-gold text-navy-900 border-gold" : "border-gold/30 text-gold hover:bg-gold/10"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Device Name</label>
                <input
                  value={newForm.device_name}
                  onChange={(e) => setNewForm((f) => ({ ...f, device_name: e.target.value }))}
                  className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white"
                  placeholder={newForm.device_type === "controller" ? "e.g. PS5 DualSense" : "e.g. PlayStation 5"}
                />
              </div>

              {/* Issues */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-2">Issues</label>
                <div className="space-y-2">
                  {REPAIR_OPTIONS[newForm.device_type].map((issue) => {
                    const selected = newForm.issues.find((i) => i.id === issue.id);
                    return (
                      <div key={issue.id}>
                        <button
                          onClick={() => toggleNewIssue(issue)}
                          className={`w-full text-left text-xs font-bold px-3 py-2 rounded-lg border transition ${selected ? "bg-gold/10 border-gold text-gold" : "border-gold/20 text-slate-300 hover:border-gold/40"}`}
                        >
                          {issue.label}
                        </button>
                        {selected && issue.hall_effect && (
                          <button
                            onClick={() => toggleHallEffect(issue)}
                            className={`mt-1 ml-4 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition ${selected.hall_effect ? "bg-gold/20 border-gold/60 text-gold" : "border-gold/20 text-slate-500 hover:border-gold/40"}`}
                          >
                            ⚡ + Hall Effect upgrade
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Delivery method */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">Service Mode</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewForm((f) => ({ ...f, pickup_required: false }))}
                    className={`flex-1 text-xs font-bold uppercase tracking-wider py-2 rounded-lg border transition ${!newForm.pickup_required ? "bg-gold text-navy-900 border-gold" : "border-gold/30 text-gold hover:bg-gold/10"}`}
                  >
                    Walk-in / Drop-off
                  </button>
                  <button
                    onClick={() => setNewForm((f) => ({ ...f, pickup_required: true }))}
                    className={`flex-1 text-xs font-bold uppercase tracking-wider py-2 rounded-lg border transition ${newForm.pickup_required ? "bg-gold text-navy-900 border-gold" : "border-gold/30 text-gold hover:bg-gold/10"}`}
                  >
                    Pickup
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gold mb-1.5">
                  Notes <span className="text-slate-500 font-normal normal-case">(optional)</span>
                </label>
                <textarea
                  value={newForm.notes}
                  onChange={(e) => setNewForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full bg-navy-700 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white resize-none"
                  placeholder="Any additional details from the customer…"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowNew(false)} className="border border-gold/30 text-gold text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-gold/10 transition">
                Cancel
              </button>
              <button onClick={createRepair} disabled={creatingNew} className="flex items-center gap-2 bg-gold text-navy-900 text-xs font-extrabold uppercase tracking-wider px-6 py-2.5 rounded-full hover:bg-gold-light transition disabled:opacity-50">
                <Plus className="w-3.5 h-3.5" /> {creatingNew ? "Creating…" : "Create Entry"}
              </button>
            </div>
          </div>
        </div>
      )}

      {view === "pricing" && (
        <div className="space-y-10">
          <p className="text-sm text-slate-400">
            Set price ranges per issue. Shown on the repair form as estimates. Pickup discount (−₹150 controller / −₹300 console) is applied automatically on top.
          </p>
          {Object.entries(REPAIR_OPTIONS_KEYS).map(([deviceType, issues]) => (
            <div key={deviceType}>
              <h3 className="text-sm font-black uppercase tracking-wider text-gold mb-4">
                {deviceType === "controller" ? "Controller (drop-off +₹150)" : "Console (drop-off +₹300)"}
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {issues.map(({ id: issueId, hall_effect }) => {
                  const key = `${deviceType}_${issueId}`;
                  const heKey = `${deviceType}_${issueId}_hall_effect`;
                  const p = pricingDraft[key] ?? {};
                  const he = pricingDraft[heKey] ?? {};
                  return (
                    <div key={key} className="bg-navy-700 rounded-xl p-4 border border-gold/10 space-y-4">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">{ISSUE_LABELS[issueId]}</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Min (₹)</label>
                            <input type="number" value={p.min ?? ""} onChange={(e) => setPriceField(deviceType, issueId, "min", e.target.value)} className="w-full bg-navy-800 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="0" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Max (₹)</label>
                            <input type="number" value={p.max ?? ""} onChange={(e) => setPriceField(deviceType, issueId, "max", e.target.value)} className="w-full bg-navy-800 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="0" />
                          </div>
                        </div>
                      </div>
                      {hall_effect && (
                        <div className="border-t border-gold/10 pt-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-gold/70 mb-2">⚡ Hall Effect Add-on</div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Add-on Min (₹)</label>
                              <input type="number" value={he.min ?? ""} onChange={(e) => setPriceField(deviceType, `${issueId}_hall_effect`, "min", e.target.value)} className="w-full bg-navy-800 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="500" />
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Add-on Max (₹)</label>
                              <input type="number" value={he.max ?? ""} onChange={(e) => setPriceField(deviceType, `${issueId}_hall_effect`, "max", e.target.value)} className="w-full bg-navy-800 border border-gold/20 rounded-lg px-3 py-2 text-sm text-white" placeholder="500" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <button onClick={savePricing} disabled={savingPricing} className="flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-8 py-3 rounded-full hover:bg-gold-light transition disabled:opacity-50">
            <Save className="w-4 h-4" /> {savingPricing ? "Saving…" : "Save Pricing"}
          </button>
        </div>
      )}
    </div>
  );
}
